import { COMPOSE_CAMPAIGNS } from '@sendible/sendible360/src/assets/js/views/common/FeatureCodeConstants';
import { routeUtils } from '@sendible/common';

export type LinkType = {
  route: string;
  label: string;
  isBeta?: boolean;
  countLabel?: string;
  groupCountLabel?: string;
  count?: number | string;
  groupCount?: number | string;
  defaultCountValue?: number;
};

export type SideBarConfigurationType = {
  header: string;
  links: LinkType[];
};

export type RouteConfigurationType = {
  featureTag?: string;
  featureRestriction?: string;
  sidebar: SideBarConfigurationType;
};

export const pages = {
  Calendar: {
    base: '/calendar',
  },
  Campaigns: {
    base: '/campaigns',
  },
  MediaLibrary: {
    base: '/medialibrary',
  },
  Measure: {
    base: '/measure',
    campaigns: '/measure/campaigns',
    tiktok: '/measure/tiktok',
  },
  Reporting: {
    base: '/reporting',
  },
} as const;

export type PageBaseKeys = {
  [K in keyof typeof pages]: (typeof pages)[K]['base'];
}[keyof typeof pages];

type routesConfig = {
  [routes in PageBaseKeys]: RouteConfigurationType;
};

const scheduledSidebarLink = {
  route: '/#publish/scheduled',
  label: 'publish_sidebar_link_scheduled',
  isBeta: false,
  defaultCountValue: 0,
};
const queueSidebarLink = { route: '/#publish/queued', label: 'publish_sidebar_link_queues', isBeta: false };
const draftsSidebarLink = { route: '/#publish/drafts', label: 'publish_sidebar_link_drafts', isBeta: false };
const sentSidebarLink = { route: '/#publish/sent', label: 'publish_sidebar_link_sent', isBeta: false };
const bouncedSidebarLink = {
  route: '/#publish/bounced',
  label: 'publish_sidebar_link_undelivered',
  isBeta: false,
};
const tasksSidebarLink = {
  route: '/#publish/tasks/me',
  label: 'publish_sidebar_link_tasks',
  isBeta: false,
  countLabel: 'taskCount',
  defaultCountValue: 0,
};

export const getRoutes = (): routesConfig => {
  const isUnifiedCalendar = window.js_featureTagsManager?.isFeatureTag('unifiedCalendar');

  return {
    [pages.Calendar.base]: {
      featureTag: 'unifiedCalendar',
      sidebar: {
        header: 'header_word_publish',
        links: [
          { route: '/calendar', label: 'publish_sidebar_link_calendar', isBeta: false },
          { route: pages.Campaigns.base, label: 'publish_sidebar_link_campaigns', isBeta: false },
          queueSidebarLink,
          draftsSidebarLink,
          tasksSidebarLink,
        ],
      },
    },
    [pages.Campaigns.base]: {
      featureRestriction: COMPOSE_CAMPAIGNS,
      sidebar: {
        header: 'header_word_publish',
        links: [
          { route: '/#publish/calendar', label: 'publish_sidebar_link_calendar', isBeta: false },
          { route: pages.Campaigns.base, label: 'publish_sidebar_link_campaigns', isBeta: false },
          ...(isUnifiedCalendar ? [] : [scheduledSidebarLink]),
          queueSidebarLink,
          ...(isUnifiedCalendar ? [] : [sentSidebarLink, bouncedSidebarLink]),
          draftsSidebarLink,
          tasksSidebarLink,
        ],
      },
    },
    [pages.MediaLibrary.base]: {
      featureTag: 'mediaLibrary',
      sidebar: {
        header: 'header_word_content',
        links: [
          { route: '/medialibrary', label: 'content_sidebar_media_library', isBeta: true },
          { route: '/#content/feeds', label: 'content_sidebar_feeds' },
          { route: '/#content', label: 'content_sidebar_content' },
        ],
      },
    },
    [pages.Measure.base]: {
      sidebar: {
        header: 'header_word_content',
        links: [],
      },
    },
    [pages.Reporting.base]: {
      featureTag: 'reporting',
      sidebar: {
        header: 'new_reports_sidebar_header',
        links: [{ route: '/reporting', label: 'new_reports_sidebar_link', isBeta: true }],
      },
    },
  };
};

export const isFEV2Route = (pathname: string) => {
  const frontendV2Routes = Object.values(pages).map((p) => p.base) as string[];

  const pathWihtoutSubPaths = routeUtils.getPathWithoutSubPaths(pathname);

  return frontendV2Routes.includes(pathWihtoutSubPaths);
};
